import type { PublicStartingSoonCircle, StartingSoon } from '../../types/startingSoon';

/**
 * The signed-out notice (docs/plans/CIRCLE-STARTING-NOTICE.md §5, §7, §8):
 * pure decisions, kept apart from PublicStartingSoonHost so each is tested
 * on its own.
 */

declare global {
  interface Window {
    /** Set by app/scripts/prerender.mjs in its browser context: never render the notice into a snapshot. */
    __EE_PRERENDER__?: boolean;
  }
}

/** The notice appears this long before a circle starts (the server's StartingSoonNotice::LEAD_MINUTES). */
export const LEAD_MS = 10 * 60_000;
/** A cohort session still takes walk-ins this long after T (N4). The hourly circle closes at T (N14). */
export const COHORT_GRACE_MS = 5 * 60_000;
/** §7: on load, then every 5 min... */
export const SLOW_POLL_MS = 5 * 60_000;
/** ...then every 30 to 60 s, only inside 15 min of the returned starts_at. */
export const FAST_POLL_MS = 45_000;
export const FAST_WINDOW_MS = 15 * 60_000;

/**
 * Crawlers, automation and the prerender pass never get the notice (§5, S5):
 * it is never in a snapshot and never shown to a bot.
 */
const BOT_UA =
  /bot\b|bot\/|crawl|spider|slurp|mediapartners|bingpreview|facebookexternalhit|embedly|quora link preview|outbrain|pinterest|vkshare|w3c_validator|whatsapp|telegram|discord|slack|twitter|linkedin|applebot|yandex|baidu|duckduck|headlesschrome|lighthouse|prerender|phantomjs|puppeteer/i;

export function isAutomatedVisitor(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return true;
  if (window.__EE_PRERENDER__ === true) return true;
  if (navigator.webdriver === true) return true;
  return BOT_UA.test(navigator.userAgent || '');
}

/**
 * Every public page that loads the SPA, except the auth pages themselves
 * (§5 "Where it shows"): landing, /learn and its articles, the quiz
 * assessment, public profiles, public group pages, and the other static
 * public pages. Not /login, /register, password reset, verification, OAuth
 * callbacks, the waitlist, invites, dev or subscription pages.
 */
const PUBLIC_NOTICE_PATHS: RegExp[] = [
  /^\/$/,
  /^\/learn\/?$/,
  /^\/learn\/[^/]+\/?$/,
  /^\/quiz-assessment(\/.*)?$/,
  /^\/u\/[^/]+\/?$/,
  /^\/circles\/\d+\/?$/,
  /^\/conflict-resolution\/?$/,
  /^\/privacy\/?$/,
  /^\/terms-of-use\/?$/,
];

export function isPublicNoticePath(pathname: string): boolean {
  return PUBLIC_NOTICE_PATHS.some((re) => re.test(pathname));
}

function validHour(t: unknown): t is number {
  return (
    typeof t === 'number' && Number.isInteger(t) && /^\d{10}$/.test(String(t)) && t % 3600 === 0
  );
}

function validId(id: unknown): id is number {
  return typeof id === 'number' && Number.isInteger(id) && id > 0 && /^\d{1,10}$/.test(String(id));
}

/** When the notice for this circle stops: T for the hourly circle, T+5 for a session. */
export function windowEndMs(circle: PublicStartingSoonCircle): number {
  const starts = Date.parse(circle.starts_at);
  return 'hourly_at' in circle.join ? starts : starts + COHORT_GRACE_MS;
}

function isoZ(ms: number): string {
  return new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/**
 * The public answer as the card's notice. Join is `register` with a path the
 * app builds from validated numbers only (S4); null when the answer does not
 * validate (the notice then simply does not show).
 */
export function toPublicNotice(
  circle: PublicStartingSoonCircle | null,
  serverTime: string,
): StartingSoon | null {
  if (!circle) return null;
  const starts = Date.parse(circle.starts_at);
  if (!Number.isFinite(starts)) return null;
  const startsSec = Math.floor(starts / 1000);
  let key: string;
  let path: string;
  if ('hourly_at' in circle.join) {
    const t = circle.join.hourly_at;
    if (!validHour(t)) return null;
    key = `pub:hourly:${t}`;
    path = `/register?hourly=${t}`;
  } else {
    const id = circle.join.cohort_id;
    if (!validId(id)) return null;
    key = `pub:cohort:${id}:${startsSec}`;
    path = `/register?circle=${id}`;
  }
  return {
    circle_id: 0,
    notice_key: key,
    kind: circle.kind,
    name: circle.name,
    people_joining: circle.people_joining,
    starts_at: circle.starts_at,
    expires_at: isoZ(windowEndMs(circle)),
    server_time: serverTime,
    action: { kind: 'register', path },
  };
}

/** Inside the notice window [T-10, end) on the server's clock. */
export function inNoticeWindow(circle: PublicStartingSoonCircle, serverNowMs: number): boolean {
  const starts = Date.parse(circle.starts_at);
  return serverNowMs >= starts - LEAD_MS && serverNowMs < windowEndMs(circle);
}

/** §7 cadence: 5 min, or 45 s inside 15 min of starts_at (until the window ends). */
export function nextPollDelayMs(
  circle: PublicStartingSoonCircle | null,
  serverNowMs: number,
): number {
  if (!circle) return SLOW_POLL_MS;
  const starts = Date.parse(circle.starts_at);
  if (!Number.isFinite(starts)) return SLOW_POLL_MS;
  const fastFrom = starts - FAST_WINDOW_MS;
  if (serverNowMs >= fastFrom && serverNowMs < windowEndMs(circle)) return FAST_POLL_MS;
  const untilFast = fastFrom - serverNowMs;
  return untilFast > 0 ? Math.min(SLOW_POLL_MS, untilFast) : SLOW_POLL_MS;
}
